import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import Fader from '../../components/Loaders/Fader';
import MessagesTable from '../../components/MessagesTable/MessagesTable';
import ConstituentForm from '../../components/ConstituentForm/ConstituentForm';

import { IMPORTANCE_TYPES, USER_ROLES } from '../../../util/constants/AppConstants';
import { getConstituent } from '../../../http/constituentAPI';
import { getFeedbackByConstituentId } from '../../../http/feedbackAPI';
import { checkForEmptyString, compareDates, formatUnixDate } from '../../../util/HelperFunctions';

import ChevronIcon from '../../../assets/images/icons/chevron.svg';
import CopyIcon from '../../../assets/images/icons/copy.svg';
import './ConstituentPage.scss';

const ConstituentPage = () => {
  const [constituentLoading, setConstituentLoading] = useState(false);
  const [constituent, setConstituent] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const config = useSelector((state) => state.config.config);

  const { userInfo } = useOutletContext();

  const { id } = useParams();

  const navigate = useNavigate();

  const stafferRoleValue = USER_ROLES.find((role) => role.short === 'Constituent Staff').value;
  const managerRoleValue = USER_ROLES.find((role) => role.short === 'Manager').value;
  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  const handleGetConstituentData = async (id) => {
    setConstituentLoading(true);

    const res = await getConstituent(config, userInfo.id, id);

    if (res.status === 200) {
      if (res.data.constituent.pending) {
        navigate('/constituents');
        return;
      }
      setConstituent(res.data.constituent);
      setConstituentLoading(false);
    } else {
      console.log('ERROR: Grabbing Constituent Data');
    }
  };

  useEffect(() => {
    if (config.api && userInfo && id) {
      handleGetConstituentData(id);
    }
  }, [config, userInfo, id]);

  const handleGetConstituentMessages = async (userId, constituentId) => {
    setMessagesLoading(true);

    const res = await getFeedbackByConstituentId(config, userId, constituentId);

    if (res.status === 200) {
      setMessages(res.data.feedback);
      setMessagesLoading(false);
    } else {
      console.log('ERROR: Grabbing Constituent Emails');
    }
  };

  useEffect(() => {
    if (config && userInfo) {
      handleGetConstituentMessages(userInfo.id, id);
    }
  }, [config, userInfo]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row) => {
          const { importance } = row;
          const option = IMPORTANCE_TYPES.find((option) => option.value === importance);
          return option ? option.label : '';
        },
        id: 'importance',
        header: 'Importance',
        minSize: 70,
        grow: 1,
        filterVariant: 'select',
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.importance;
          const b = rowB.original.importance;

          if (a === b) return 0;
          if (a === null || a === undefined) return 1;
          if (b === null || b === undefined) return -1;
          return a - b;
        },
      },
      {
        accessorKey: 'type',
        header: 'Category',
        minSize: 150,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        minSize: 150,
        grow: 5,
        enableSorting: false,
      },
      {
        accessorKey: 'source',
        header: 'Source',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'staffer_fullname',
        header: 'Added by',
        minSize: 110,
        grow: 1,
        filterVariant: 'select',
        enableSorting: false,
      },
      {
        accessorKey: 'date_utc',
        header: 'Date',
        filterVariant: 'date',
        Cell: ({ cell }) => formatUnixDate(cell.getValue(), false),
        filterFn: (row, columnId, filterValue) => compareDates(new Date(row.getValue(columnId) * 1000), filterValue.$d),
        minSize: 150,
        grow: 1,
      },
    ];

    if (isMpp || isCouncillor) {
      return [
        {
          accessorKey: 'status',
          header: 'Status',
          minSize: 70,
          grow: 1,
          filterVariant: 'select',
        },
        ...baseColumns,
      ];
    }

    if (userInfo && (userInfo.role === stafferRoleValue || userInfo.role === managerRoleValue)) {
      return [
        {
          accessorFn: (row) => {
            const { status } = row;
            return status === 'Unread' || status === 'Read' ? 'Unhandled' : status;
          },
          id: 'status',
          header: 'Status',
          minSize: 70,
          grow: 1,
          filterVariant: 'select',
        },
        ...baseColumns,
      ];
    }

    return baseColumns;
  }, [userInfo]);

  const handleEditConstituent = () => {
    setIsModalOpen(true);
  };

  const handleCloseForm = () => {
    setIsModalOpen(false);
  };

  const handleCopy = (value, index) => {
    if (value !== '') {
      navigator.clipboard.writeText(value).then(() => {
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 1000);
      });
    }
  };

  if (constituentLoading || !constituent) {
    return (
      <div className="wrap">
        <Fader />
      </div>
    );
  }

  return (
    <div className="constituent">
      <div className="message__header">
        <div className="message__back" onClick={() => navigate('/constituents')}>
          <img src={ChevronIcon} alt="Back" />
          Back to Constituents
        </div>
        <div className="message__control">
          <Tooltip title="Edit">
            <IconButton
              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
              onClick={handleEditConstituent}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className="message__info">
        <h2 className="message__title">{constituent.name || '(No name)'}</h2>
        <div className="message__information">
          <div className="message__line">
            <span>Email:</span>
            <span className="message__email-list">
              {constituent.email
                ? constituent.email.split(';').map((email, idx) => {
                    const trimmed = email.trim();
                    return (
                      <span
                        key={idx}
                        className={`message__email ${trimmed === '' ? 'empty' : ''}`}
                        onClick={() => handleCopy(trimmed, idx)}
                      >
                        {trimmed !== '' && (
                          <Tooltip title="Copy">
                            <img src={CopyIcon} alt="Copy" />
                          </Tooltip>
                        )}
                        {checkForEmptyString(trimmed)}
                        {copiedIndex === idx && <span className="message__notify">Email copied</span>}
                      </span>
                    );
                  })
                : '-'}
            </span>
          </div>
          <div className="message__line">
            <span>Phone:</span>
            <span>{checkForEmptyString(constituent.phone)}</span>
          </div>
          <div className="message__line">
            <span>Contact Count:</span>
            <span>{constituent.contact_count}</span>
          </div>
        </div>
      </div>
      <div className="constituent__address">
        <h2 className="message__title">Address</h2>
        <p className="constituent__address-line">
          {isCouncillor ? `Ward ${constituent.ward_label ?? '...'}` : `Poll ${constituent.poll_label ?? '...'}`}
          {'\u00A0'}— {constituent.postal_code}
          {'\u00A0'}— {constituent.address === '' ? '...' : constituent.address}
        </p>
        <button className="constituent__history-toggle" onClick={() => setShowHistory((prev) => !prev)}>
          {showHistory ? 'Hide history' : 'Show history'}
        </button>
        {showHistory &&
          (constituent.move_history?.length > 0 ? (
            <div className="timeline">
              <div className="timeline__item">
                <div className="timeline__marker"></div>
                <div className="timeline__content">
                  <p className="constituent__address-line">
                    {isCouncillor
                      ? `Ward ${constituent.move_history[0].ward_label}`
                      : `Poll ${constituent.move_history[0].poll_label}`}
                    {'\u00A0'}— {constituent.move_history[0].postal_code}
                    {'\u00A0'}— {constituent.move_history[0].address}
                  </p>
                  <p className="timeline__date">
                    {new Date(constituent.move_history[0].date_created).toLocaleDateString()}
                  </p>
                </div>
              </div>
              {constituent.move_history.map((move, i) => (
                <div className="timeline__item" key={move.id}>
                  <div className="timeline__marker"></div>
                  <div className="timeline__content">
                    <p className="constituent__address-line">
                      {isCouncillor ? `Ward ${move.last_ward_label}` : `Poll ${move.last_poll_label}`}
                      {'\u00A0'}— {move.last_postal_code}
                      {'\u00A0'}— {move.last_address}
                    </p>
                    {i < constituent.move_history.length - 1 && (
                      <p className="timeline__date">
                        {new Date(constituent.move_history[i + 1].date_created).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="constituent__no-history">No move history</p>
          ))}
      </div>
      <div className="constituent__messages">
        <h2 className="message__title">Emails</h2>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MessagesTable data={messages} columns={columns} loading={messagesLoading} />
        </LocalizationProvider>
      </div>
      {isModalOpen && (
        <ConstituentForm
          isOpen={isModalOpen}
          onCloseHandler={handleCloseForm}
          constituentData={constituent}
          handleGetConstituentData={handleGetConstituentData}
        />
      )}
    </div>
  );
};

export default ConstituentPage;
